import { NotificationRecord } from '../../../redux/types/notifications';

export type NotificationTarget = Pick<
  NotificationRecord,
  'clientId' | 'projectId' | 'sourceId' | 'messageId' | 'sourceType'
>;

interface BuildClientManagementPathOptions {
  currentPathname?: string;
  currentSearch?: string;
  focusAlertMessage?: boolean;
}

const NOTIFICATION_CHAT_QUERY_KEYS = [
  'focusMessageId',
  'notificationChat',
  'notificationReturnTo',
  'returnContext',
  'sourceId',
  'sourceType',
] as const;

export function clearNotificationChatParams(search: string): URLSearchParams {
  const searchParams = new URLSearchParams(search);
  NOTIFICATION_CHAT_QUERY_KEYS.forEach((key) => searchParams.delete(key));
  searchParams.delete('messageId');
  return searchParams;
}

export function getNotificationFocusMessageId(search: string): number | null {
  const searchParams = new URLSearchParams(search);
  if (searchParams.get('returnContext') !== 'notifications' || searchParams.get('sourceType') !== 'alert') {
    return null;
  }

  const messageId = Number(searchParams.get('focusMessageId'));
  return Number.isInteger(messageId) && messageId > 0 ? messageId : null;
}

export function getNotificationReturnPath(search: string): string | null {
  const returnTo = new URLSearchParams(search).get('notificationReturnTo');
  if (returnTo?.startsWith('/funnel')) return returnTo;
  return null;
}

export function getNotificationIntentKey(search: string): string | null {
  const searchParams = new URLSearchParams(search);
  if (searchParams.get('returnContext') !== 'notifications') return null;

  const sourceType = searchParams.get('sourceType') ?? '';
  const sourceId = searchParams.get('sourceId') ?? `client:${searchParams.get('client') ?? ''}`;
  const notificationChat = searchParams.get('notificationChat') ?? '';
  const focusMessageId = searchParams.get('focusMessageId') ?? '';
  const notificationReturnTo = searchParams.get('notificationReturnTo') ?? '';

  return [sourceType, sourceId, notificationChat, focusMessageId, notificationReturnTo].join('|');
}

export function buildClientManagementPath(
  notif: NotificationTarget,
  options: BuildClientManagementPathOptions = {}
): string {
  const { clientId, projectId, sourceId, messageId, sourceType } = notif;
  const { currentPathname, currentSearch = '', focusAlertMessage = false } = options;
  const isOnFunnel = Boolean(currentPathname?.startsWith('/funnel'));

  if (clientId === null || clientId === undefined) {
    throw new Error('Notification does not include clientId.');
  }

  const searchParams = clearNotificationChatParams(currentSearch);
  if (isOnFunnel) {
    const returnSearch = searchParams.toString();
    searchParams.set('notificationReturnTo', `${currentPathname}${returnSearch ? `?${returnSearch}` : ''}`);
  }
  searchParams.set('client', String(clientId));
  searchParams.set('details', 'true');
  searchParams.set('returnContext', 'notifications');
  searchParams.set('sourceType', sourceType);

  if (sourceId != null) searchParams.set('sourceId', String(sourceId));

  if (projectId !== null && projectId !== undefined) searchParams.set('projectId', String(projectId));

  if (!isOnFunnel) {
    searchParams.set('view', 'chat');
    searchParams.delete('chat');
  } else {
    searchParams.delete('chat');
    if (searchParams.get('view') !== 'chat') searchParams.set('notificationChat', 'side');
  }

  if (focusAlertMessage && sourceType === 'alert' && messageId != null) {
    searchParams.set('focusMessageId', String(messageId));
  }

  return `/funnel?${searchParams.toString()}`;
}
